import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Contact, BookOpen, Inbox, Webhook, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip, BarChart, Bar, YAxis } from "recharts";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

function KPI({ icon: Icon, label, value, testId }) {
  return (
    <Card className="rounded-[16px] card-hover" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
          <Icon className="h-4 w-4 text-[#BFA76A]" />
        </div>
        <p className="mt-2 font-mono-num text-3xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-[16px]" />)}</div>
        <Skeleton className="h-72 rounded-[16px]" />
      </div>
    );
  }

  const totals = data?.totals || {};
  const pipelineData = Object.entries(data?.leads_by_pipeline || {}).map(([name, count]) => ({ name, count }));
  const timeline = (data?.submissions_timeline || []).map((d) => ({ ...d, label: d.date.slice(5) }));

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Eleven Admin Center</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("Plattform-Kennzahlen auf einen Blick", "Platform metrics at a glance")}</p>
        </div>
        <Link to="/admin/crm" data-testid="admin-crm-status-link">
          <Badge variant="outline" className={`rounded-full px-3 py-1.5 gap-1.5 ${data?.crm?.configured ? "border-emerald-500/40 text-emerald-600" : "border-slate-400/40 text-slate-500"}`}>
            <Webhook className="h-3.5 w-3.5" />
            GoHighLevel: {data?.crm?.configured ? t("Aktiv", "Active") : t("Nicht konfiguriert", "Not configured")}
            {data?.crm?.pending_events > 0 && ` \u00b7 ${data.crm.pending_events} ${t("Events wartend", "events pending")}`}
          </Badge>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Contact} label="Leads" value={totals.leads ?? 0} testId="admin-kpi-leads" />
        <KPI icon={Inbox} label={t("Formular-Eing\u00e4nge", "Form submissions")} value={totals.submissions ?? 0} testId="admin-kpi-submissions" />
        <KPI icon={Users} label={t("Aktive Nutzer", "Active users")} value={totals.users ?? 0} testId="admin-kpi-users" />
        <KPI icon={BookOpen} label={t("Kurse", "Courses")} value={totals.courses ?? 0} testId="admin-kpi-courses" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-[16px]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">{t("Anfragen der letzten 14 Tage", "Inquiries over the last 14 days")}</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeline}>
                <defs>
                  <linearGradient id="subGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8EC5E8" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#8EC5E8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#5B9BC8" strokeWidth={2} fill="url(#subGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="rounded-[16px]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">{t("Leads nach Pipeline", "Leads by pipeline")}</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pipelineData} layout="vertical" margin={{ left: 30 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} tickLine={false} axisLine={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#0B1220" radius={[0, 6, 6, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { to: "/admin/benutzer", label: t("Benutzer verwalten", "Manage users") },
          { to: "/admin/kurse", label: t("Kurse verwalten", "Manage courses") },
          { to: "/admin/einstellungen", label: t("System-Einstellungen", "System settings") },
        ].map((l) => (
          <Link key={l.to} to={l.to} className="rounded-[14px] border border-border bg-white p-5 flex items-center justify-between card-hover" data-testid={`admin-quicklink-${l.to.split("/").pop()}`}>
            <span className="text-sm font-medium">{l.label}</span>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
          </Link>
        ))}
      </div>
    </div>
  );
}
